"use client";

import { type ReactNode } from "react";
import { useInView } from "@/lib/use-in-view";
import { cn } from "@/lib/cn";

type Props = {
  children: ReactNode;
  /** ms delay before this element transitions in (used for stagger) */
  delay?: number;
  className?: string;
  /** Tailwind class for the off-screen translate. Default: translate-y-3 (12 px). */
  translate?: string;
};

/**
 * One-shot scroll-reveal wrapper. Renders children with `opacity-0` and a
 * subtle 12px translate-y until the element scrolls into view, then fades up.
 * GPU-accelerated (opacity + transform only). Respects prefers-reduced-motion
 * via useInView's instant-true behavior.
 */
export function Reveal({ children, delay = 0, className, translate = "translate-y-3" }: Props) {
  const [ref, inView] = useInView<HTMLDivElement>();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: inView ? `${delay}ms` : "0ms" }}
      className={cn(
        "transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none",
        inView ? "translate-y-0 opacity-100" : `${translate} opacity-0`,
        className,
      )}
    >
      {children}
    </div>
  );
}
